import { useDarkMode } from "../ThemeContext";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";
import amrutbinduData from "../data/amrutbindu.json";

const AMRUTBINDU_INDEX_URL = "/data/amrutbindu-index.json";
const LAST_READ_KEY = "amrutbindu-last-read";

const CHAPTERS = [
  { id: 1, name: "અમૃત બિંદુ 1" },
  { id: 2, name: "અમૃત બિંદુ 2" },
  { id: 3, name: "અમૃત બિંદુ 3" },
  { id: 4, name: "અમૃત બિંદુ 4" },
  { id: 5, name: "અમૃત બિંદુ 5" },
];

const ITEMS = [...amrutbinduData];
const chapterNameFor = (chapterId) => {
  const match = CHAPTERS.find((chapter) => chapter.id === chapterId);
  if (match) return match.name;
  const itemMatch = ITEMS.find((item) => item.chapterid === chapterId);
  return itemMatch?.chaptername || "Amrutbindu";
};

const ROMAN_CONSONANTS = {
  ksh: "ક્ષ",
  chh: "છ",
  kh: "ખ",
  gh: "ઘ",
  ch: "ચ",
  jh: "ઝ",
  th: "થ",
  dh: "ધ",
  ph: "ફ",
  bh: "ભ",
  sh: "શ",
  gn: "જ્ઞ",
  tr: "ત્ર",
  ny: "ન્ય",
  ng: "ઙ",
  k: "ક",
  g: "ગ",
  c: "ક",
  j: "જ",
  t: "ત",
  d: "દ",
  n: "ન",
  p: "પ",
  b: "બ",
  m: "મ",
  y: "ય",
  r: "ર",
  l: "લ",
  v: "વ",
  w: "વ",
  s: "સ",
  h: "હ",
  f: "ફ",
  q: "ક",
  x: "ક્સ",
  z: "ઝ",
};
const ROMAN_VOWELS = {
  aa: { independent: "આ", matra: "ા" },
  ai: { independent: "ઐ", matra: "ૈ" },
  au: { independent: "ઔ", matra: "ૌ" },
  ii: { independent: "ઈ", matra: "ી" },
  ee: { independent: "ઈ", matra: "ી" },
  oo: { independent: "ઊ", matra: "ૂ" },
  uu: { independent: "ઊ", matra: "ૂ" },
  a: { independent: "અ", matra: "" },
  i: { independent: "ઇ", matra: "િ" },
  u: { independent: "ઉ", matra: "ુ" },
  e: { independent: "એ", matra: "ે" },
  o: { independent: "ઓ", matra: "ો" },
};
const GUJARATI_ROMAN = {
  "અ": "a",
  "આ": "aa",
  "ઇ": "i",
  "ઈ": "i",
  "ઉ": "u",
  "ઊ": "u",
  "ઋ": "ri",
  "એ": "e",
  "ઐ": "ai",
  "ઓ": "o",
  "ઔ": "au",
  "ક": "k",
  "ખ": "kh",
  "ગ": "g",
  "ઘ": "gh",
  "ઙ": "ng",
  "ચ": "ch",
  "છ": "chh",
  "જ": "j",
  "ઝ": "jh",
  "ઞ": "ny",
  "ટ": "t",
  "ઠ": "th",
  "ડ": "d",
  "ઢ": "dh",
  "ણ": "n",
  "ત": "t",
  "થ": "th",
  "દ": "d",
  "ધ": "dh",
  "ન": "n",
  "પ": "p",
  "ફ": "ph",
  "બ": "b",
  "ભ": "bh",
  "મ": "m",
  "ય": "y",
  "ર": "r",
  "લ": "l",
  "ળ": "l",
  "વ": "v",
  "શ": "sh",
  "ષ": "sh",
  "સ": "s",
  "હ": "h",
};
const GUJARATI_MATRAS = {
  "ા": "aa",
  "િ": "i",
  "ી": "i",
  "ુ": "u",
  "ૂ": "u",
  "ૃ": "ri",
  "ે": "e",
  "ૈ": "ai",
  "ો": "o",
  "ૌ": "au",
};

function writeCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function readCookie(name) {
  const pair = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair ? decodeURIComponent(pair.slice(name.length + 1)) : "";
}

function saveLastRead(item) {
  const value = JSON.stringify({
    id: item.id,
    filename: item.filename,
    title: item.title,
    chapterid: item.chapterid,
  });
  try {
    localStorage.setItem(LAST_READ_KEY, value);
  } catch {
    // Cookie fallback is used when local storage is unavailable.
  }
  writeCookie(LAST_READ_KEY, value);
}

function loadLastRead() {
  let value = "";
  try {
    value = localStorage.getItem(LAST_READ_KEY) || "";
  } catch {
    // Fall back to cookie below.
  }
  if (!value) value = readCookie(LAST_READ_KEY);
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed?.filename ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeRoman(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/aa/g, "a")
    .replace(/ee|ii/g, "i")
    .replace(/oo|uu/g, "u")
    .replace(/sh/g, "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/chh/g, "ch")
    .replace(/v/g, "w");
}

function normalizeGujarati(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[િી]/g, "િ")
    .replace(/[ુૂ]/g, "ુ")
    .replace(/[ેૈ]/g, "ે")
    .replace(/[ોૌ]/g, "ો")
    .replace(/[ંઃઁ]/g, "ં")
    .replace(/[શષસ]/g, "સ")
    .replace(/ળ/g, "લ");
}

function romanToGujarati(text) {
  const input = (text || "").toLowerCase().replace(/[^a-z\s]/g, "");
  const vowels = Object.keys(ROMAN_VOWELS).sort((a, b) => b.length - a.length);
  const consonants = Object.keys(ROMAN_CONSONANTS).sort(
    (a, b) => b.length - a.length,
  );
  let output = "";
  let index = 0;
  while (index < input.length) {
    if (/\s/.test(input[index])) {
      output += " ";
      index += 1;
      continue;
    }
    const remaining = input.slice(index);
    const vowel = vowels.find((token) => remaining.startsWith(token));
    if (vowel) {
      output += ROMAN_VOWELS[vowel].independent;
      index += vowel.length;
      continue;
    }
    const consonant = consonants.find((token) => remaining.startsWith(token));
    if (!consonant) {
      index += 1;
      continue;
    }
    output += ROMAN_CONSONANTS[consonant];
    index += consonant.length;
    const afterConsonant = input.slice(index);
    const nextVowel = vowels.find((token) => afterConsonant.startsWith(token));
    if (nextVowel) {
      output += ROMAN_VOWELS[nextVowel].matra;
      index += nextVowel.length;
    }
  }
  return output;
}

function gujaratiToRoman(text) {
  const input = text || "";
  let output = "";
  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];
    const next = input[index + 1] || "";
    if (/[\u0A95-\u0AB9]/.test(character)) {
      const consonant = GUJARATI_ROMAN[character];
      if (!consonant) continue;
      if (GUJARATI_MATRAS[next]) {
        output += consonant + GUJARATI_MATRAS[next];
        index += 1;
      } else if (next === "્") {
        output += consonant;
        index += 1;
      } else {
        output += `${consonant}a`;
      }
      continue;
    }
    if (GUJARATI_ROMAN[character]) {
      output += GUJARATI_ROMAN[character];
      continue;
    }
    if (GUJARATI_MATRAS[character]) {
      output += GUJARATI_MATRAS[character];
      continue;
    }
    if (character === "ં" || character === "ઁ") output += "m";
    else if (character === "ઃ") output += "h";
    else output += " ";
  }
  return normalizeRoman(output.replace(/a(\s|$)/g, "$1"));
}

function buildSearchPayload(query) {
  const trimmed = (query || "").trim();
  const terms = new Set([trimmed.toLowerCase()]);
  const highlightTerms = new Set([trimmed]);
  const isGujarati = /[\u0A80-\u0AFF]/.test(trimmed);
  const isRoman = !isGujarati && /^[a-z\s]+$/i.test(trimmed);
  if (isGujarati) {
    terms.add(normalizeGujarati(trimmed));
  } else if (isRoman) {
    const transliterated = romanToGujarati(trimmed);
    if (transliterated) {
      terms.add(transliterated.toLowerCase());
      terms.add(normalizeGujarati(transliterated));
      highlightTerms.add(transliterated);
    }
  }
  return {
    terms: Array.from(terms).filter(Boolean),
    highlightTerms: Array.from(highlightTerms).filter(Boolean),
    isRoman,
    romanQuery: normalizeRoman(trimmed),
  };
}

function HighlightedText({ text, word }) {
  if (!word || word.trim().length < 2) return <>{text}</>;
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = (text || "").split(regex);
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? <mark key={index}>{part}</mark> : part,
      )}
    </>
  );
}

export default function Amrutbindu() {
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [search, setSearch] = useState("");
  const [textSearch, setTextSearch] = useState("");
  const [textSearchResults, setTextSearchResults] = useState(null);
  const [isTextSearching, setIsTextSearching] = useState(false);
  const [indexLoading, setIndexLoading] = useState(true);
  const [indexError, setIndexError] = useState("");
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [contentHighlightWord, setContentHighlightWord] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fontSize, setFontSize] = useState(1);
  const [lastRead, setLastRead] = useState(() => loadLastRead());
  const [darkMode, setDarkMode] = useDarkMode();
  const [contentIndex, setContentIndex] = useState([]);
  const activeRef = useRef(null);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.PUBLIC_URL}${AMRUTBINDU_INDEX_URL}`)
      .then((response) => {
        if (!response.ok) throw new Error("Could not load the Amrutbindu search index.");
        return response.json();
      })
      .then((data) => {
        if (active) {
          setContentIndex(data);
          setIndexLoading(false);
        }
      })
      .catch((error) => {
        if (active) {
          setIndexError(error.message);
          setIndexLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return ITEMS.filter((item) => {
      if (selectedChapter !== null && item.chapterid !== selectedChapter) {
        return false;
      }
      if (textSearchResults !== null && !textSearchResults.has(item.filename)) {
        return false;
      }
      return (
        !query ||
        item.title.toLowerCase().includes(query) ||
        (item.chaptername && item.chaptername.toLowerCase().includes(query)) ||
        chapterNameFor(item.chapterid).toLowerCase().includes(query) ||
        String(item.chapterid).includes(query)
      );
    });
  }, [search, selectedChapter, textSearchResults]);

  const selectedIndex = useMemo(
    () => filtered.findIndex((item) => item.id === selected?.id),
    [filtered, selected],
  );

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const openItem = useCallback(
    async (item) => {
      setSelected(item);
      setLastRead({
        id: item.id,
        filename: item.filename,
        title: item.title,
        chapterid: item.chapterid,
      });
      saveLastRead(item);
      setContent("");
      setFontSize(1);
      setIsLoading(true);

      try {
        const response = await fetch(
          `${process.env.PUBLIC_URL}/data/amrutbindu/${item.filename}`,
        );
        if (!response.ok) throw new Error("File not found");
        const text = await response.text();
        const { highlightTerms } = buildSearchPayload(textSearch);
        const highlightWord =
          highlightTerms.find(
            (term) =>
              term &&
              term.trim().length >= 2 &&
              text.toLowerCase().includes(term.toLowerCase()),
          ) || textSearch.trim();
        setContent(text);
        setContentHighlightWord(highlightWord);
      } catch {
        setContent("Content could not be loaded.");
        setContentHighlightWord("");
      } finally {
        setIsLoading(false);
      }
    },
    [textSearch],
  );

  const goToRelativeItem = useCallback(
    (delta) => {
      if (selectedIndex < 0) return;
      const target = filtered[selectedIndex + delta];
      if (target) openItem(target);
    },
    [filtered, openItem, selectedIndex],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const tag = (target?.tagName || "").toLowerCase();
      if (
        !selected ||
        ["input", "textarea", "select", "button"].includes(tag) ||
        target?.isContentEditable
      ) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToRelativeItem(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goToRelativeItem(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goToRelativeItem, selected]);

  const searchTextFiles = () => {
    const query = textSearch.trim();
    if (query.length < 2 || indexLoading || indexError) return;

    setIsTextSearching(true);
    try {
      const { terms, isRoman, romanQuery } = buildSearchPayload(query);
      const matches = new Set(
        contentIndex
          .filter((entry) => {
            const lowerText = entry.text.toLowerCase();
            const normalizedText = normalizeGujarati(entry.text);
            const matchesText = terms.some(
              (term) => lowerText.includes(term) || normalizedText.includes(term),
            );
            return (
              matchesText ||
              (isRoman &&
                romanQuery &&
                gujaratiToRoman(entry.text).includes(romanQuery))
            );
          })
          .map((entry) => entry.file),
      );
      setTextSearchResults(matches);
    } catch {
      setTextSearchResults(new Set());
    } finally {
      setIsTextSearching(false);
    }
  };

  const clearTextSearch = () => {
    setTextSearch("");
    setTextSearchResults(null);
  };

  const openLastRead = useCallback(() => {
    if (!lastRead) return;
    const item = ITEMS.find(
      (candidate) =>
        (lastRead.id && candidate.id === lastRead.id) ||
        candidate.filename === lastRead.filename,
    );
    if (item) openItem(item);
  }, [lastRead, openItem]);

  const lastReadChapterId = useMemo(() => {
    if (!lastRead) return "";
    if (lastRead.chapterid) return String(lastRead.chapterid);
    const item = ITEMS.find(
      (candidate) =>
        (lastRead.id && candidate.id === lastRead.id) ||
        candidate.filename === lastRead.filename,
    );
    return item ? String(item.chapterid) : "";
  }, [lastRead]);

  return (
    <main
      className={`page-content pravachan-page prite-page amrutbindu-page scripture-reader${darkMode ? " sv-dark" : ""}`}
    >
      <section className="page-header">
        <h1 className="section-title">
          Amrutbindu
          <span className="prite-count">({filtered.length})</span>
        </h1>
        <p className="header-subtitle">અમૃતબિંદુ</p>
        {lastRead && (
          <button
            type="button"
            className="last-read-vat"
            onClick={openLastRead}
            title="Open last read Amrutbindu"
          >
            Last Read: Chapter {lastReadChapterId} · {lastRead.title}
          </button>
        )}
      </section>

      <section className="page-body">
        <div className="word-search-bar" style={{ marginBottom: 10 }}>
          <input
            type="text"
            className="word-search-input"
            placeholder="Search Amrutbindu text (Gujarati or English)..."
            value={textSearch}
            onChange={(event) => setTextSearch(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && searchTextFiles()}
          />
          <button
            type="button"
            className="word-search-btn"
            onClick={searchTextFiles}
            disabled={textSearch.trim().length < 2 || indexLoading || Boolean(indexError)}
          >
            {indexLoading
              ? "Loading index..."
              : isTextSearching
                ? "Searching..."
                : "Search Text"}
          </button>
          {textSearchResults !== null && (
            <button
              type="button"
              className="word-search-clear"
              onClick={clearTextSearch}
            >
              Clear ({textSearchResults.size})
            </button>
          )}
          <button
            type="button"
            className="word-search-clear"
            onClick={() => setDarkMode((value) => !value)}
            title="Toggle light/dark mode"
          >
            {darkMode ? "Light" : "Dark"}
          </button>
        </div>
        {indexError && (
          <p className="prite-search-error" role="alert">
            Text search unavailable: {indexError}
          </p>
        )}
        {textSearchResults !== null && (
          <p className="prite-search-count" aria-live="polite">
            {textSearchResults.size} result{textSearchResults.size === 1 ? "" : "s"} for "
            {textSearch.trim()}"
          </p>
        )}

        <div style={{ display: "grid", gap: 14, marginBottom: 20 }}>
          <div className="prite-chapter-list" role="group" aria-label="Chapters" style={chapterListStyle}>
            <button
              type="button"
              onClick={() => setSelectedChapter(null)}
              aria-pressed={selectedChapter === null}
              style={chapterButtonStyle(selectedChapter === null)}
            >
              બધા (All)
            </button>
            {CHAPTERS.map((chapter) => (
              <button
                type="button"
                key={chapter.id}
                onClick={() =>
                  setSelectedChapter(selectedChapter === chapter.id ? null : chapter.id)
                }
                style={chapterButtonStyle(selectedChapter === chapter.id)}
                title={chapter.name}
                aria-pressed={selectedChapter === chapter.id}
              >
                {chapter.name}
              </button>
            ))}
          </div>
        </div>

        <div className="vachanamrut-body prite-layout">
          <aside className="vachanamrut-list-panel prite-list-panel">
            <input
              className="vachanamrut-search"
              type="text"
              placeholder="Filter list..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <ul className="vachanamrut-list">
              {filtered.map((item) => {
                const isActive = selected?.id === item.id;
                return (
                  <li
                    key={item.id}
                    ref={isActive ? activeRef : null}
                    className={`vachanamrut-item prite-item${isActive ? " active" : ""}`}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => openItem(item)}
                  >
                    <span className="vachan-name">{item.chaptername || chapterNameFor(item.chapterid)}</span>
                    <span className="vachan-name-guj">{item.title}</span>
                  </li>
                );
              })}
            </ul>
            {filtered.length === 0 && (
              <p className="prite-empty" style={{ textAlign: "center", color: "#888", padding: 24 }}>
                No entries found.
              </p>
            )}
          </aside>

          <section className="vachanamrut-content-panel prite-content-panel">
            {!selected ? (
              <div className="vachanamrut-placeholder">
                <p>વાંચવાં માટે ડાબી બાજુથી પ્રકરણ પસંદ કરો.</p>
                <p>Select an entry from the list to read.</p>
              </div>
            ) : (
              <article className="vachanamrut-article">
                <div className="prite-toolbar" style={readerToolbarStyle}>
                  <span>{selected.chaptername || chapterNameFor(selected.chapterid)}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => goToRelativeItem(-1)}
                      disabled={selectedIndex <= 0}
                      style={fontButtonStyle}
                      title="Previous entry (Left Arrow)"
                    >
                      ← Prev
                    </button>
                    <button
                      type="button"
                      onClick={() => goToRelativeItem(1)}
                      disabled={selectedIndex < 0 || selectedIndex >= filtered.length - 1}
                      style={fontButtonStyle}
                      title="Next entry (Right Arrow)"
                    >
                      Next →
                    </button>
                    <button
                      type="button"
                      onClick={() => setFontSize((size) => Math.max(size - 0.15, 0.7))}
                      style={fontButtonStyle}
                    >
                      A-
                    </button>
                    <button
                      type="button"
                      onClick={() => setFontSize((size) => Math.min(size + 0.15, 2.5))}
                      style={fontButtonStyle}
                    >
                      A+
                    </button>
                  </div>
                </div>
                <h2 className="vachan-title">{selected.title}</h2>
                {isLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner" />
                    <p>Loading...</p>
                  </div>
                ) : (
                  <div className="prite-content-text" style={{ ...contentStyle, fontSize: `${fontSize}em` }}>
                    <HighlightedText text={content} word={contentHighlightWord} />
                  </div>
                )}
              </article>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}

const chapterListStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  justifyContent: "center",
};

const readerToolbarStyle = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  marginBottom: 16,
  color: "#4a2472",
  fontWeight: 700,
};

const contentStyle = {
  lineHeight: 2,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "'Noto Sans Gujarati', sans-serif",
};

function chapterButtonStyle(active) {
  return {
    padding: "6px 14px",
    borderRadius: 16,
    border: "1.5px solid #764ba2",
    background: active ? "#764ba2" : "#f0e8ff",
    color: active ? "#fff" : "#4a2472",
    fontWeight: 600,
    fontSize: "0.88em",
    cursor: "pointer",
  };
}

const fontButtonStyle = {
  padding: "2px 8px",
  fontSize: "0.82em",
  fontWeight: 700,
  cursor: "pointer",
  background: "#e8f0fe",
  border: "1px solid #764ba2",
  borderRadius: 5,
  color: "#764ba2",
};
